import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { ChromeTooltip } from './ChromeTooltip'

/**
 * 缩略图等场景：悬浮「气泡」删除按钮。
 * - 使用全局主题变量（--panel / --line / --surface-muted / shadow-card），随 data-theme 变化。
 * - angular：直角 + 粗描边 + 像素风偏移阴影；否则圆形胶囊 + shadow-card。
 */
export function floatingRemoveBubbleClass(angular: boolean): string {
  return [
    'inline-flex size-5 shrink-0 items-center justify-center text-xs font-bold leading-none text-ink antialiased outline-none transition',
    'border border-line bg-[color-mix(in_srgb,var(--panel)_93%,transparent)] backdrop-blur-[3px]',
    'shadow-card',
    'hover:border-red-500/75 hover:bg-surface-muted hover:text-red-600',
    'active:scale-[0.94]',
    'focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-muted)]',
    angular
      ? [
          'rounded-none border-2',
          'shadow-[4px_4px_0_color-mix(in_srgb,var(--line)_50%,transparent)]',
          'hover:shadow-[3px_3px_0_color-mix(in_srgb,var(--line)_50%,transparent)]',
        ].join(' ')
      : 'rounded-full',
  ].join(' ')
}

export type FloatingRemoveBubbleProps = {
  angular: boolean
  /** 叠在锚点右上角内侧，常用：`right-1 top-1`，勿再 `translate-*` 顶出滚动容器以免横向溢出 */
  className?: string
  /** 悬停时主题 tooltip（与 ChromeTooltip 一致），勿再用原生 `title` 叠样式 */
  tooltipContent?: ReactNode
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'type'>

export function FloatingRemoveBubble({
  angular,
  className = '',
  children = '×',
  tooltipContent,
  ...rest
}: FloatingRemoveBubbleProps) {
  const btn = (
    <button
      type="button"
      className={floatingRemoveBubbleClass(angular)}
      {...rest}
    >
      {children}
    </button>
  )
  if (tooltipContent == null || tooltipContent === '') {
    return (
      <button
        type="button"
        className={[floatingRemoveBubbleClass(angular), className].filter(Boolean).join(' ')}
        {...rest}
      >
        {children}
      </button>
    )
  }
  return (
    <ChromeTooltip
      angular={angular}
      content={tooltipContent}
      side="bottom"
      align="end"
      className={['inline-flex', className].filter(Boolean).join(' ')}
    >
      {btn}
    </ChromeTooltip>
  )
}
